import { UAParser } from 'ua-parser-js';
import * as Sentry from '@sentry/nextjs';

export type SessionParams = {
  landingPage?: string;
  webReferrer?: string;
};

const STORAGE_KEY = 'session_params';

export const persistLandingPage = (override = false) => {
  if (typeof sessionStorage === 'undefined') return;
  const params = getSessionPageParams();

  if (params.landingPage && !override) return;

  params.landingPage = window.location.pathname;

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
};

export const persistWebReferrer = (webReferrer: string, override = true) => {
  if (typeof sessionStorage === 'undefined') return;
  const params = getSessionPageParams();

  if (params.webReferrer && !override) return;

  params.webReferrer = webReferrer;

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
};

const getSessionPageParams = () => {
  if (typeof sessionStorage === 'undefined') return {};
  const storedParams = sessionStorage.getItem(STORAGE_KEY) || '';
  try {
    return JSON.parse(storedParams) as SessionParams;
  } catch (_err: any) {
    // Exception ignored - storage is probably empty
  }
  return {};
};

const platformMap = {
  'mac os': 'Macintosh',
  windows: 'Windows',
  android: 'Android',
};

export const getSessionParams = () => {
  if (typeof window === 'undefined') return {};

  const params = getSessionPageParams();

  try {
    const uaResult = new UAParser(window.navigator.userAgent).getResult();

    let osName = uaResult.os.name;
    if (osName) {
      const name = osName;
      const transformedName = Object.entries(platformMap).find(([match]) => {
        return new RegExp('^' + match + '$', 'i').test(name);
      });

      if (transformedName) osName = transformedName[1];
    }

    const deviceType = uaResult.device.type || 'desktop';

    return { ...params, platform: osName, device: deviceType };
  } catch (e) {
    Sentry.captureMessage(
      'Failed to extract user agent session parameters' + e,
      'error',
    );
    Sentry.captureException(e);
  }

  return params;
};
